import { useCallback, useEffect, useRef, useState } from 'react'
import {
    Image,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from 'react-native'
import Svg, { Path, Polyline, Line } from 'react-native-svg'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { selectCurrentUser } from '../store/selectors/authSelectors'
import { logout } from '../store/slices/authSlice'
import { addNotification } from '../store/slices/uiSlice'
import * as streamingApi from '../api/streaming.api'
import { COLORS, RADIUS, SPACING, TYPOGRAPHY } from '../constants/theme'
import { useAppTranslation } from '../i18n/useAppTranslation'
import { selectTables } from '../store/selectors/tableSelectors'
import ShotClockBand, { StopwatchIcon } from './shotClock/ShotClockBand'
import { ShotClockSnapshot, useHotkeys, useShotClock } from '../hooks/useShotClock'
import { isShotClockMuted, setShotClockMuted, unlockShotClockAudio } from '../utils/shotClockSound'
import { formatCountdown, matchRemainingAt } from '../utils/matchClockUtils'

const POLL_INTERVAL_MS = 10_000
const EXTENDED_STORAGE_KEY = 'scoreboard.extendedMode'

const readExtendedPref = (): boolean => {
    try {
        return typeof localStorage !== 'undefined' && localStorage.getItem(EXTENDED_STORAGE_KEY) === '1'
    } catch {
        return false
    }
}

const writeExtendedPref = (value: boolean) => {
    try {
        if (typeof localStorage !== 'undefined') localStorage.setItem(EXTENDED_STORAGE_KEY, value ? '1' : '0')
    } catch {
        // ignore: private mode / blocked storage
    }
}

// eslint-disable-next-line @typescript-eslint/no-var-requires
const clubLogo = require('../assets/clubs/cluster/logo.png')

interface ConfirmState {
    message: string
    onConfirm: () => void
}

const ServiceScoreBoard = () => {
    const { t } = useAppTranslation()
    const dispatch = useAppDispatch()
    const currentUser = useAppSelector(selectCurrentUser)
    const tables = useAppSelector(selectTables)
    const tableId = currentUser?.serviceTableId ?? null

    const table = tableId != null ? tables.find(tb => tb.id === tableId) : null
    const tableName = table?.name ?? (tableId != null ? `${t('serviceScore.tableLabel')} ${tableId}` : '')

    const [session, setSession] = useState<streamingApi.YtStreamSession | null>(null)
    const [confirm, setConfirm] = useState<ConfirmState | null>(null)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [extended, setExtended] = useState<boolean>(readExtendedPref)
    const [muted, setMuted] = useState<boolean>(isShotClockMuted())
    const [showKeyHints, setShowKeyHints] = useState(false)

    const [timeoutBusy, setTimeoutBusy] = useState(false)
    const [nowMs, setNowMs] = useState(() => Date.now())
    const sessionReceivedAtRef = useRef(Date.now())

    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

    // The session carries the match time left as of when it was fetched; count down from receipt.
    useEffect(() => {
        sessionReceivedAtRef.current = Date.now()
        setNowMs(Date.now())
    }, [session])

    const matchClockOn = extended && !!session?.matchDurationMinutes
    useEffect(() => {
        if (!matchClockOn) return
        const id = setInterval(() => setNowMs(Date.now()), 1000)
        return () => clearInterval(id)
    }, [matchClockOn])

    const matchMs = session && session.matchDurationMinutes
        ? matchRemainingAt({
            matchDurationMinutes: session.matchDurationMinutes,
            matchRemainingMs: session.matchRemainingMs,
            matchRunning: session.matchRunning,
            breakStartedAtMs: null,
            serverNowMs: 0,
            receivedAtMs: sessionReceivedAtRef.current,
        }, nowMs)
        : null

    const fetchSession = useCallback(async () => {
        if (tableId == null) return
        try {
            const s = await streamingApi.getActiveSession(tableId)
            setSession(s)
        } catch {
            setSession(null)
        }
    }, [tableId])

    useEffect(() => {
        fetchSession()
        pollRef.current = setInterval(fetchSession, POLL_INTERVAL_MS)
        return () => {
            if (pollRef.current) clearInterval(pollRef.current)
        }
    }, [fetchSession])

    // Mirror every operator command to the backend (overlay + persisted flags).
    // Fire-and-forget: a failed call must never block or delay the clock.
    const sendClockCommand = useCallback((snap: ShotClockSnapshot) => {
        if (tableId == null) return
        streamingApi.sendShotClock(tableId, {
            action: snap.action,
            activePlayer: snap.activePlayer,
            remainingMs: snap.remainingMs,
            player1ExtAvailable: snap.extAvailable[0],
            player2ExtAvailable: snap.extAvailable[1],
        }).catch(() => {})
    }, [tableId])

    const shotClock = useShotClock({ enabled: extended, onCommand: sendClockCommand })
    const { hydrate: hydrateShotClock } = shotClock

    const sessionId = session?.id ?? null
    useEffect(() => {
        if (!session) return
        hydrateShotClock(
            session.shotClockActivePlayer ?? 1,
            [session.player1ExtAvailable ?? true, session.player2ExtAvailable ?? true],
        )
        // Only when a (new) session appears; polls must not override the live clock.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId])

    // First time extended mode is used on a session: publish the starting state so the
    // overlay shows the +30s clock icons from the very beginning of the game.
    const announcedSessionRef = useRef<string | null>(null)
    useEffect(() => {
        if (!extended || !session || tableId == null) return
        if (session.shotClockActivePlayer != null || announcedSessionRef.current === session.id) return
        announcedSessionRef.current = session.id
        streamingApi.sendShotClock(tableId, {
            action: 'GAME_RESET',
            activePlayer: shotClock.state.activePlayer,
            remainingMs: 0,
            player1ExtAvailable: shotClock.state.extAvailable[0],
            player2ExtAvailable: shotClock.state.extAvailable[1],
        }).then(s => setSession(s)).catch(() => {})
    }, [extended, session, tableId, shotClock.state.activePlayer, shotClock.state.extAvailable])

    const toggleExtended = useCallback(() => {
        setExtended(prev => {
            const next = !prev
            writeExtendedPref(next)
            return next
        })
        unlockShotClockAudio()
    }, [])

    const toggleMuted = useCallback(() => {
        const next = !isShotClockMuted()
        setShotClockMuted(next)
        setMuted(next)
    }, [])

    useEffect(() => {
        if (typeof document === 'undefined') return
        const onChange = () => setIsFullscreen(!!document.fullscreenElement)
        document.addEventListener('fullscreenchange', onChange)
        return () => document.removeEventListener('fullscreenchange', onChange)
    }, [])

    // iOS Safari never implements the Fullscreen API for page content (only
    // for <video>), on any iOS version — requestFullscreen is simply absent
    // there, so calling it unguarded throws synchronously.
    const fullscreenSupported = typeof document !== 'undefined'
        && typeof document.documentElement.requestFullscreen === 'function'

    const toggleFullscreen = () => {
        if (!fullscreenSupported) return
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(() => {})
        } else {
            document.exitFullscreen().catch(() => {})
        }
    }

    const handleLogout = () => {
        setConfirm({
            message: t('serviceScore.confirmLogout'),
            onConfirm: () => {
                setConfirm(null)
                dispatch(logout())
            },
        })
    }

    // Clock keys only exist in extended mode; X always toggles the mode.
    useHotkeys(
        extended
            ? {
                ' ': shotClock.reset,
                Enter: () => shotClock.switchPlayer(),
                p: shotClock.toggle,
                e: shotClock.extend,
                m: toggleMuted,
                x: toggleExtended,
                f: toggleFullscreen,
                Escape: toggleExtended,
            }
            : { x: toggleExtended, f: toggleFullscreen },
        !confirm,
        () => setShowKeyHints(true),
    )

    const adjustGameScore = async (
        field: 'player1GameScore' | 'player2GameScore',
        delta: number,
    ) => {
        if (tableId == null || !session) return
        const updated = {
            player1Wins: session.player1Wins,
            player2Wins: session.player2Wins,
            player1GameScore: session.player1GameScore,
            player2GameScore: session.player2GameScore,
            [field]: session[field] + delta,
        }
        try {
            const s = await streamingApi.updateScore(tableId, updated)
            setSession(s)
        } catch {
            dispatch(addNotification({ type: 'error', message: t('common.error') }))
        }
    }

    const adjustMatchWins = (player: 1 | 2, delta: number) => {
        if (tableId == null || !session) return
        const playerName = player === 1 ? session.player1Name : session.player2Name
        const message = delta > 0
            ? (t('streaming.confirm.winsPlus') as string).replace('{{name}}', playerName)
            : t('streaming.confirm.winsMinus')

        setConfirm({
            message,
            onConfirm: async () => {
                setConfirm(null)
                const field = player === 1 ? 'player1Wins' : 'player2Wins'
                const updated = {
                    player1Wins: session.player1Wins,
                    player2Wins: session.player2Wins,
                    player1GameScore: 0,
                    player2GameScore: 0,
                    [field]: Math.max(0, session[field] + delta),
                }
                try {
                    const s = await streamingApi.updateScore(tableId, updated)
                    setSession(s)
                    // A win change ends the game: restore +30s icons and the extended shot after the break.
                    if (extended) shotClock.resetGame()
                } catch {
                    dispatch(addNotification({ type: 'error', message: t('common.error') }))
                }
            },
        })
    }

    // TimeOut = the stream break (pause overlay on the stream); the match clock keeps running.
    const toggleTimeout = async () => {
        if (tableId == null || !session || timeoutBusy) return
        setTimeoutBusy(true)
        try {
            const s = session.isBreak
                ? await streamingApi.stopBreak(tableId)
                : await streamingApi.startBreak(tableId)
            setSession(s)
        } catch {
            dispatch(addNotification({ type: 'error', message: t('common.error') }))
        } finally {
            setTimeoutBusy(false)
        }
    }

    const hasActiveStream = session &&(session.status === 'LIVE' || session.status === 'PREPARING')

    return (
        <View style={styles.root}>
            {/* Top-left corner: extended scoreboard (shot clock) toggle */}
            <View style={styles.topLeftCorner} pointerEvents="box-none">
                <TouchableOpacity
                    style={[styles.fullscreenBtn, extended && styles.toggleBtnActive]}
                    onPress={toggleExtended}
                    accessibilityLabel={extended ? t('serviceScore.extended.toggleOff') : t('serviceScore.extended.toggleOn')}
                >
                    <StopwatchIcon
                        color={extended ? COLORS.scoreboard.gold : COLORS.scoreboard.goldDim}
                    />
                </TouchableOpacity>
            </View>

            {/* Top-right corner: logout + fullscreen buttons + club logo */}
            <View style={styles.topRightCorner} pointerEvents="box-none">
                <TouchableOpacity style={styles.fullscreenBtn} onPress={handleLogout}>
                    <Svg width={18} height={18} viewBox="0 0 24 24" accessibilityElementsHidden>
                        <Path
                            d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
                            stroke={COLORS.scoreboard.goldDim}
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fill="none"
                        />
                        <Polyline
                            points="16 17 21 12 16 7"
                            stroke={COLORS.scoreboard.goldDim}
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fill="none"
                        />
                        <Line
                            x1="21"
                            y1="12"
                            x2="9"
                            y2="12"
                            stroke={COLORS.scoreboard.goldDim}
                            strokeWidth={2}
                            strokeLinecap="round"
                        />
                    </Svg>
                </TouchableOpacity>
                {fullscreenSupported && (
                    <TouchableOpacity style={styles.fullscreenBtn} onPress={toggleFullscreen}>
                        <Text style={styles.fullscreenBtnText}>{isFullscreen ? '⊡' : '⛶'}</Text>
                    </TouchableOpacity>
                )}
                <View pointerEvents="none">
                    <Image source={clubLogo} style={styles.logo} resizeMode="contain" />
                </View>
            </View>

            {/* Confirmation dialog */}
            {confirm && (
                <View style={styles.confirmOverlay}>
                    <View style={styles.confirmBox}>
                        <Text style={styles.confirmMessage}>{confirm.message}</Text>
                        <View style={styles.confirmButtons}>
                            <TouchableOpacity
                                style={styles.confirmCancelBtn}
                                onPress={() => setConfirm(null)}
                            >
                                <Text style={styles.confirmCancelText}>{t('streaming.confirm.cancelBtn')}</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={styles.confirmOkBtn}
                                onPress={confirm.onConfirm}
                            >
                                <Text style={styles.confirmOkText}>{t('streaming.confirm.confirmBtn')}</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            )}

            {!hasActiveStream ? (
                /* No-stream state */
                <View style={styles.noStreamWrap}>
                    <Text style={styles.noStreamTableName}>{tableName}</Text>
                    <View style={styles.diamondSep}>
                        <View style={styles.separatorLine} />
                        <Text style={styles.diamond}>◆</Text>
                        <View style={styles.separatorLine} />
                    </View>
                    <Text style={styles.noStreamText}>{t('serviceScore.noStream')}</Text>
                    <Text style={styles.noStreamHint}>{t('serviceScore.noStreamHint')}</Text>
                </View>
            ) : (
                /* Active stream state */
                <ScrollView style={styles.scroll} contentContainerStyle={styles.scoreboardWrap}>
                    {/* Tournament header */}
                    <View style={styles.headerSection}>
                        {session.tournamentName ? (
                            <Text style={styles.tournamentName}>{session.tournamentName.toUpperCase()}</Text>
                        ) : null}
                        {session.stage ? (
                            <View style={styles.stageSepRow}>
                                <View style={styles.separatorLine} />
                                <Text style={styles.stageText}>{session.stage}</Text>
                                <View style={styles.separatorLine} />
                            </View>
                        ) : null}
                        {extended && matchMs != null && (
                            <Text style={[styles.matchTime, matchMs === 0 && styles.matchTimeExpired]}>
                                {t('serviceScore.extended.matchTime')} {formatCountdown(matchMs)}
                            </Text>
                        )}
                    </View>

                    {/* Extended mode: shot clock band */}
                    {extended && (
                        <ShotClockBand
                            player1Name={session.player1Name}
                            player2Name={session.player2Name}
                            state={shotClock.state}
                            seconds={shotClock.seconds}
                            remainingMs={shotClock.remainingMs}
                            tone={shotClock.tone}
                            muted={muted}
                            showKeyHints={showKeyHints}
                            labels={{
                                reset: t('serviceScore.extended.reset'),
                                extend: t('serviceScore.extended.extend'),
                                switchPlayer: t('serviceScore.extended.switchPlayer'),
                                start: t('serviceScore.extended.start'),
                                pause: t('serviceScore.extended.pause'),
                                resume: t('serviceScore.extended.resume'),
                                expired: t('serviceScore.extended.expired'),
                                extAvailable: t('serviceScore.extended.extAvailable'),
                                soundOn: t('serviceScore.extended.sound'),
                                soundOff: t('serviceScore.extended.soundOff'),
                                timeout: t('serviceScore.extended.timeout'),
                                timeoutEnd: t('serviceScore.extended.timeoutEnd'),
                            }}
                            timeoutActive={session.isBreak}
                            timeoutBusy={timeoutBusy}
                            onTimeout={toggleTimeout}
                            onToggle={shotClock.toggle}
                            onReset={shotClock.reset}
                            onExtend={shotClock.extend}
                            onSwitch={shotClock.switchPlayer}
                            onToggleMute={toggleMuted}
                        />
                    )}

                    {/* Match score section */}
                    <View style={styles.matchRow}>
                        {/* Player 1 */}
                        <View style={styles.playerCol}>
                            <Text style={styles.playerName} numberOfLines={2}>
                                {session.player1Name.toUpperCase()}
                            </Text>
                            <Text style={styles.scoreLabel}>{t('serviceScore.matchScore')}</Text>
                            <View style={styles.scoreControls}>
                                <TouchableOpacity
                                    style={styles.scoreBtn}
                                    onPress={() => adjustMatchWins(1, -1)}
                                >
                                    <Text style={styles.scoreBtnText}>−</Text>
                                </TouchableOpacity>
                                <Text style={styles.matchScoreValue}>{session.player1Wins}</Text>
                                <TouchableOpacity
                                    style={styles.scoreBtn}
                                    onPress={() => adjustMatchWins(1, 1)}
                                >
                                    <Text style={styles.scoreBtnText}>+</Text>
                                </TouchableOpacity>
                            </View>
                        </View>

                        {/* VS */}
                        <Text style={styles.vs}>VS</Text>

                        {/* Player 2 */}
                        <View style={styles.playerCol}>
                            <Text style={styles.playerName} numberOfLines={2}>
                                {session.player2Name.toUpperCase()}
                            </Text>
                            <Text style={styles.scoreLabel}>{t('serviceScore.matchScore')}</Text>
                            <View style={styles.scoreControls}>
                                <TouchableOpacity
                                    style={styles.scoreBtn}
                                    onPress={() => adjustMatchWins(2, -1)}
                                >
                                    <Text style={styles.scoreBtnText}>−</Text>
                                </TouchableOpacity>
                                <Text style={styles.matchScoreValue}>{session.player2Wins}</Text>
                                <TouchableOpacity
                                    style={styles.scoreBtn}
                                    onPress={() => adjustMatchWins(2, 1)}
                                >
                                    <Text style={styles.scoreBtnText}>+</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>

                    {/* Divider */}
                    <View style={styles.dividerRow}>
                        <View style={styles.dividerLine} />
                        <Text style={styles.diamond}>◆</Text>
                        <View style={styles.dividerLine} />
                    </View>

                    {/* Game score section */}
                    <View style={styles.gameScoreRow}>
                        <View style={styles.gameScoreCol}>
                            <Text style={styles.scoreLabel}>{t('serviceScore.gameScore')}</Text>
                            <View style={styles.scoreControls}>
                                <TouchableOpacity
                                    style={styles.gameBtnLarge}
                                    onPress={() => adjustGameScore('player1GameScore', -1)}
                                >
                                    <Text style={styles.gameBtnText}>−</Text>
                                </TouchableOpacity>
                                <Text style={styles.gameScoreValue}>{session.player1GameScore}</Text>
                                <TouchableOpacity
                                    style={styles.gameBtnLarge}
                                    onPress={() => adjustGameScore('player1GameScore', 1)}
                                >
                                    <Text style={styles.gameBtnText}>+</Text>
                                </TouchableOpacity>
                            </View>
                        </View>

                        <View style={styles.gameScoreCol}>
                            <Text style={styles.scoreLabel}>{t('serviceScore.gameScore')}</Text>
                            <View style={styles.scoreControls}>
                                <TouchableOpacity
                                    style={styles.gameBtnLarge}
                                    onPress={() => adjustGameScore('player2GameScore', -1)}
                                >
                                    <Text style={styles.gameBtnText}>−</Text>
                                </TouchableOpacity>
                                <Text style={styles.gameScoreValue}>{session.player2GameScore}</Text>
                                <TouchableOpacity
                                    style={styles.gameBtnLarge}
                                    onPress={() => adjustGameScore('player2GameScore', 1)}
                                >
                                    <Text style={styles.gameBtnText}>+</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>

                    {/* Bottom pyramid balls decoration */}
                    <View style={styles.bottomDecoration}>
                        <View style={styles.dividerLine} />
                        <Text style={styles.pyramidDecor}>▲ ▲ ▲</Text>
                        <View style={styles.dividerLine} />
                    </View>
                </ScrollView>
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    root: {
        flex: 1,
        backgroundColor: COLORS.scoreboard.background,
        position: 'relative',
        // Kiosk device: avoid iOS long-press text selection/callout popups
        // interrupting live score entry.
        userSelect: 'none',
    },
    topLeftCorner: {
        position: 'absolute',
        top: SPACING.lg,
        left: SPACING.lg,
        zIndex: 10,
    },
    toggleBtnActive: {
        borderColor: COLORS.scoreboard.gold,
    },
    topRightCorner: {
        position: 'absolute',
        top: SPACING.lg,
        right: SPACING.lg,
        zIndex: 10,
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: SPACING.sm,
    },
    fullscreenBtn: {
        width: 36,
        height: 36,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: COLORS.scoreboard.panelBg,
        borderRadius: RADIUS.md,
        borderWidth: 1,
        borderColor: COLORS.scoreboard.divider,
    },
    fullscreenBtnText: {
        color: COLORS.scoreboard.goldDim,
        fontSize: 18,
        lineHeight: 20,
    },
    logo: {
        width: 80,
        height: 80,
    },

    // Confirmation dialog
    confirmOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
    },
    confirmBox: {
        backgroundColor: COLORS.scoreboard.panelBg,
        borderRadius: RADIUS.lg,
        padding: SPACING.xl,
        gap: SPACING.lg,
        maxWidth: 400,
        width: '90%',
        borderWidth: 1,
        borderColor: COLORS.scoreboard.divider,
    },
    confirmMessage: {
        color: COLORS.scoreboard.gold,
        fontSize: TYPOGRAPHY.fontSize.lg,
        textAlign: 'center',
    },
    confirmButtons: {
        flexDirection: 'row',
        gap: SPACING.md,
    },
    confirmCancelBtn: {
        flex: 1,
        height: 48,
        borderRadius: RADIUS.md,
        borderWidth: 1,
        borderColor: COLORS.scoreboard.divider,
        alignItems: 'center',
        justifyContent: 'center',
    },
    confirmCancelText: {
        color: COLORS.scoreboard.goldDim,
        fontSize: TYPOGRAPHY.fontSize.md,
    },
    confirmOkBtn: {
        flex: 1,
        height: 48,
        borderRadius: RADIUS.md,
        backgroundColor: COLORS.scoreboard.panelBg,
        borderWidth: 1,
        borderColor: COLORS.scoreboard.gold,
        alignItems: 'center',
        justifyContent: 'center',
    },
    confirmOkText: {
        color: COLORS.scoreboard.gold,
        fontSize: TYPOGRAPHY.fontSize.md,
        fontWeight: TYPOGRAPHY.fontWeight.bold,
    },

    // No-stream state
    noStreamWrap: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        gap: SPACING.md,
        paddingHorizontal: SPACING.xl,
    },
    noStreamTableName: {
        color: COLORS.scoreboard.gold,
        fontSize: TYPOGRAPHY.fontSize['2xl'],
        fontWeight: TYPOGRAPHY.fontWeight.bold,
        textAlign: 'center',
        letterSpacing: 2,
    },
    noStreamText: {
        color: COLORS.scoreboard.gold,
        fontSize: TYPOGRAPHY.fontSize.xl,
        textAlign: 'center',
        marginTop: SPACING.md,
    },
    noStreamHint: {
        color: COLORS.scoreboard.goldDim,
        fontSize: TYPOGRAPHY.fontSize.md,
        textAlign: 'center',
    },

    // Active scoreboard — spreads all sections to fill full screen height; scrolls when they don't fit
    scroll: {
        flex: 1,
    },
    scoreboardWrap: {
        flexGrow: 1,
        paddingHorizontal: SPACING.xl,
        paddingTop: SPACING.xl,
        paddingBottom: SPACING.xl,
        justifyContent: 'space-between',
        gap: SPACING.lg,
    },
    matchTime: {
        color: COLORS.scoreboard.goldDim,
        fontSize: TYPOGRAPHY.fontSize.sm,
        fontWeight: TYPOGRAPHY.fontWeight.semibold,
        letterSpacing: 2,
        fontVariant: ['tabular-nums'],
        textAlign: 'center',
    },
    matchTimeExpired: {
        color: COLORS.scoreboard.clockCritical,
    },
    headerSection: {
        alignItems: 'center',
        gap: SPACING.sm,
    },
    tournamentName: {
        color: COLORS.scoreboard.gold,
        fontSize: TYPOGRAPHY.fontSize['3xl'] ?? 36,
        fontWeight: TYPOGRAPHY.fontWeight.bold,
        textAlign: 'center',
        letterSpacing: 3,
    },
    stageSepRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
        width: '60%',
    },
    stageText: {
        color: COLORS.scoreboard.goldDim,
        fontSize: TYPOGRAPHY.fontSize.md,
        letterSpacing: 2,
        textAlign: 'center',
    },

    // Match row — players centered in their columns
    matchRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    playerCol: {
        flex: 1,
        alignItems: 'center',
        gap: SPACING.sm,
    },
    playerName: {
        color: COLORS.scoreboard.gold,
        fontSize: TYPOGRAPHY.fontSize['2xl'],
        fontWeight: TYPOGRAPHY.fontWeight.bold,
        letterSpacing: 1,
        textAlign: 'center',
    },
    scoreLabel: {
        color: COLORS.scoreboard.goldDim,
        fontSize: TYPOGRAPHY.fontSize.xs,
        fontWeight: TYPOGRAPHY.fontWeight.semibold,
        letterSpacing: 1,
        textAlign: 'center',
    },
    scoreControls: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.md,
    },
    scoreBtn: {
        width: 72,
        height: 72,
        backgroundColor: COLORS.scoreboard.panelBg,
        borderRadius: RADIUS.lg,
        alignItems: 'center',
        justifyContent: 'center',
    },
    scoreBtnText: {
        color: COLORS.scoreboard.gold,
        fontSize: 36,
        fontWeight: TYPOGRAPHY.fontWeight.bold,
        lineHeight: 38,
    },
    matchScoreValue: {
        color: COLORS.scoreboard.gold,
        fontSize: TYPOGRAPHY.fontSize['4xl'] ?? 48,
        fontWeight: TYPOGRAPHY.fontWeight.bold,
        minWidth: 56,
        textAlign: 'center',
    },
    vs: {
        color: COLORS.scoreboard.goldDim,
        fontSize: TYPOGRAPHY.fontSize.xl,
        fontWeight: TYPOGRAPHY.fontWeight.bold,
        marginHorizontal: SPACING.md,
    },

    // Divider
    dividerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
    },
    dividerLine: {
        flex: 1,
        height: 1,
        backgroundColor: COLORS.scoreboard.divider,
    },
    diamond: {
        color: COLORS.scoreboard.goldDim,
        fontSize: 12,
    },

    // Game score section — columns centered
    gameScoreRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: SPACING.xl,
    },
    gameScoreCol: {
        flex: 1,
        alignItems: 'center',
        gap: SPACING.sm,
    },
    gameBtnLarge: {
        width: 92,
        height: 92,
        backgroundColor: COLORS.scoreboard.panelBg,
        borderRadius: RADIUS.lg,
        alignItems: 'center',
        justifyContent: 'center',
    },
    gameBtnText: {
        color: COLORS.scoreboard.gold,
        fontSize: 44,
        fontWeight: TYPOGRAPHY.fontWeight.bold,
        lineHeight: 46,
    },
    gameScoreValue: {
        color: COLORS.scoreboard.gold,
        fontSize: 72,
        fontWeight: TYPOGRAPHY.fontWeight.bold,
        minWidth: 72,
        textAlign: 'center',
    },

    // Bottom decoration
    bottomDecoration: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
    },
    pyramidDecor: {
        color: COLORS.scoreboard.goldDim,
        fontSize: 10,
        letterSpacing: 4,
    },

    // Separator used in no-stream and stage
    separatorLine: {
        flex: 1,
        height: 1,
        backgroundColor: COLORS.scoreboard.divider,
    },
    diamondSep: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
        width: '40%',
        marginVertical: SPACING.sm,
    },
})

export default ServiceScoreBoard
